import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const config = {
    success: { icon: CheckCircle2, className: "bg-signal-success/10 text-signal-success border-signal-success/30" },
    failed: { icon: XCircle, className: "bg-signal-error/10 text-signal-error border-signal-error/30" },
    running: { icon: RefreshCw, className: "bg-signal-info/10 text-signal-info border-signal-info/30" }
  };
  
  const { icon: Icon, className } = config[status] || config.running;
  
  return (
    <Badge variant="outline" className={`${className} flex items-center gap-1`}>
      <Icon className={`w-3 h-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {status}
    </Badge>
  );
};

export const SyncHistory = () => {
  const [history, setHistory] = useState([]);
  const [enrollees, setEnrollees] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [historyRes, enrolleesRes] = await Promise.all([
        axios.get(`${API}/sync/history?limit=30`),
        axios.get(`${API}/enrollees/new?limit=50`)
      ]);
      setHistory(historyRes.data);
      setEnrollees(enrolleesRes.data);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    try {
      return new Date(isoString).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      });
    } catch {
      return isoString;
    }
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return "-";
    try {
      const ms = new Date(end) - new Date(start);
      const seconds = Math.floor(ms / 1000);
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m ${seconds % 60}s`;
    } catch {
      return "-";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-forest animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 fade-in" data-testid="sync-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-stone-900 tracking-tight">Sync History</h1>
          <p className="text-stone-500 mt-1">View past sync runs and newly detected enrollees</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Sync Runs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading">Sync Runs</CardTitle>
          <CardDescription>History of CampMinder sync operations</CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          {history.length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Fetched</TableHead>
                  <TableHead className="text-center">New</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Date Window</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell className="font-mono text-sm">{formatDate(run.started_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{run.run_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={run.status} />
                    </TableCell>
                    <TableCell className="text-center">{run.records_fetched}</TableCell>
                    <TableCell className="text-center">
                      {run.new_records_found > 0 ? (
                        <Badge className="bg-signal-success text-white">{run.new_records_found}</Badge>
                      ) : (
                        <span className="text-stone-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-stone-500">
                      {formatDuration(run.started_at, run.completed_at)}
                    </TableCell>
                    <TableCell className="text-sm text-stone-500">
                      {run.date_window_start || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-stone-500">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No sync runs yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Enrollees */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading">Recently Detected Enrollees</CardTitle>
          <CardDescription>New campers found during sync operations</CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          {enrollees.length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Detected</TableHead>
                  <TableHead>Sheets Sync</TableHead>
                  <TableHead>Parent Webhook</TableHead>
                  <TableHead>T-Shirt Digest</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrollees.map((enrollee) => (
                  <TableRow key={enrollee.id}>
                    <TableCell className="font-mono text-sm">{enrollee.person_id}</TableCell>
                    <TableCell className="font-medium">
                      {enrollee.last_name}, {enrollee.first_name}
                    </TableCell>
                    <TableCell className="text-sm text-stone-500">
                      {formatDate(enrollee.created_at)}
                    </TableCell>
                    <TableCell>
                      {enrollee.sheet_synced_at ? (
                        <CheckCircle2 className="w-4 h-4 text-signal-success" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-signal-warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      {enrollee.parent_webhook_sent_at ? (
                        <CheckCircle2 className="w-4 h-4 text-signal-success" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-signal-warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      {enrollee.tshirt_digest_included_at ? (
                        <CheckCircle2 className="w-4 h-4 text-signal-success" />
                      ) : (
                        <Clock className="w-4 h-4 text-stone-400" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-stone-500">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No new enrollees detected yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
